import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSellerAdminPath, getSellerName } from '../utils/seller';
import { influencerPublicName } from '../utils/person';
import './ProductDetails.css';

const getProductDisplayStatus = (item) => {
  const status = (item?.status || 'default').toLowerCase();
  const expiresAt = item?.expires_at ? new Date(item.expires_at) : null;
  const isExpired = !!(
    expiresAt &&
    !Number.isNaN(expiresAt.getTime()) &&
    expiresAt < new Date() &&
    status !== 'sold' &&
    status !== 'rejected'
  );
  return isExpired ? 'expired' : status;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const DetailSection = ({ title, children }) => (
  <section className="product-detail-section">
    <div className="product-detail-section-head">
      <h3>{title}</h3>
    </div>
    <div className="product-detail-section-body">{children}</div>
  </section>
);

const ProductDetails = ({ product, onEdit, readOnly }) => {
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);

  const sortedImages = useMemo(() => {
    if (!product?.images?.length) return [];
    return [...product.images].sort(
      (a, b) => (a.image_order ?? 0) - (b.image_order ?? 0)
    );
  }, [product?.images]);

  if (!product) return null;

  const displayStatus = getProductDisplayStatus(product);
  const closet = product.is_closet_product;
  const coverUrl = sortedImages[activeImage]?.image_url || null;

  const sellerName = getSellerName(product.seller);
  const sellerPath = getSellerAdminPath(product.seller);

  const influencerLabel = product.influencer
    ? influencerPublicName(product.influencer)
    : null;

  return (
    <div className="product-detail">
      <div className="product-detail-hero">
        <div className="product-detail-gallery">
          <div className="product-detail-cover">
            {coverUrl ? (
              <img src={coverUrl} alt={product.title || 'Product'} />
            ) : (
              <span className="product-detail-cover-empty">No images uploaded</span>
            )}
          </div>
          {sortedImages.length > 1 ? (
            <div className="product-detail-thumbs">
              {sortedImages.map((img, index) => (
                <button
                  key={img.id || index}
                  type="button"
                  className={`product-detail-thumb ${index === activeImage ? 'active' : ''}`}
                  onClick={() => setActiveImage(index)}
                  aria-label={`Image ${index + 1}`}
                >
                  <img src={img.image_url} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="product-detail-summary">
          <div className="product-detail-summary-card">
            <p className="product-detail-price">
              {product.currency || 'AED'} {product.price ?? '0'}
            </p>
            <span className={`status-badge status-${displayStatus}`}>{displayStatus}</span>
            <span className={`product-type-pill ${closet ? 'closet' : ''}`}>
              {closet ? 'Closet' : 'Marketplace'}
            </span>
            {product.is_verified ? (
              <div className="product-detail-verified">
                ✓ Verified in app
                {product.verification_image_url ? (
                  <img src={product.verification_image_url} alt="Badge" />
                ) : null}
              </div>
            ) : null}
            <div className="product-detail-summary-row">
              <strong>Expires:</strong> {formatDateTime(product.expires_at)}
            </div>
            <div className="product-detail-summary-row">
              <strong>Created:</strong> {formatDateTime(product.created_at)}
            </div>
          </div>

          {!readOnly && onEdit ? (
            <div className="product-detail-actions">
              <button type="button" className="btn btn-primary" onClick={onEdit}>
                Edit product
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="product-detail-layout">
        <div>
          <DetailSection title="About">
            <p className="product-detail-description">
              {product.description?.trim() || (
                <span className="product-detail-description-muted">No description</span>
              )}
            </p>
            {product.description_ar?.trim() ? (
              <>
                <h4
                  style={{
                    margin: '16px 0 8px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                  }}
                >
                  Arabic
                </h4>
                <p className="product-detail-description" dir="rtl">
                  {product.description_ar}
                </p>
              </>
            ) : null}
          </DetailSection>

          {(product.title_ar?.trim()) ? (
            <DetailSection title="Titles">
              <dl className="product-detail-dl">
                <div>
                  <dt>English</dt>
                  <dd>{product.title}</dd>
                </div>
                <div>
                  <dt>Arabic</dt>
                  <dd dir="rtl">{product.title_ar}</dd>
                </div>
              </dl>
            </DetailSection>
          ) : null}
        </div>

        <aside className="product-detail-sidebar-stack">
          <DetailSection title="Catalog">
            <dl className="product-detail-dl">
              <div>
                <dt>Category</dt>
                <dd>{product.category?.name || product.category || '—'}</dd>
              </div>
              <div>
                <dt>Brand</dt>
                <dd>{product.brand || '—'}</dd>
              </div>
              <div>
                <dt>Condition</dt>
                <dd>
                  {product.condition?.display_name ||
                    product.condition?.name ||
                    product.condition ||
                    '—'}
                </dd>
              </div>
            </dl>
          </DetailSection>

          <DetailSection title="Attributes">
            <dl className="product-detail-dl">
              <div>
                <dt>Color</dt>
                <dd>{product.color?.name || product.color || '—'}</dd>
              </div>
              <div>
                <dt>Size</dt>
                <dd>{product.size || '—'}</dd>
              </div>
              <div>
                <dt>Location</dt>
                <dd>{product.location?.name || product.location || '—'}</dd>
              </div>
              <div>
                <dt>Gender</dt>
                <dd>{product.gender || '—'}</dd>
              </div>
            </dl>
          </DetailSection>

          <DetailSection title="Listing">
            <dl className="product-detail-dl">
              <div>
                <dt>Seller</dt>
                <dd>
                  {product.seller?.id && sellerPath ? (
                    <button
                      type="button"
                      className="product-detail-seller-link"
                      onClick={() => navigate(sellerPath)}
                    >
                      {sellerName}
                    </button>
                  ) : (
                    sellerName
                  )}
                </dd>
              </div>
              {influencerLabel ? (
                <div>
                  <dt>Closet account</dt>
                  <dd>{influencerLabel}</dd>
                </div>
              ) : null}
              <div>
                <dt>Product ID</dt>
                <dd style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  {product.id || product.uuid || '—'}
                </dd>
              </div>
            </dl>
          </DetailSection>
        </aside>
      </div>
    </div>
  );
};

export default ProductDetails;
